import _ from 'underscore';
import { ISiteSettingsForServerAdminFormScope } from '../../settings/controllers/SiteSettingsForServerAdminFormCtrl';
import { SettingsFormService } from '../../settings/directives/tbSettingsCoordinator';
import { AskDataModeFromString } from '../../settings/types/AskDataMode';
import { ISiteSettingsForServerAdmin } from '../../settings/types/ISite';
import { ISiteAdminSettings } from '../../settings/types/ISiteSettingsForSiteAdmin';
import { FeatureFlags } from '../FeatureFlags';
import { L10n } from '../L10n/L10n';
import { Module } from '../Module';
import { defaultSiteSettingsForServerAdmin, defaultSiteSettingsForSiteAdmin, defaultSubscriptionSettingsForSite, Sites } from '../siteManagement/services/resources/Sites';
import { IError } from '../../httpClient/serverApi/types/IError';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ModalSize } from './ModalService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { ToasterService, ToastType } from './ToasterService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';

export class CreateSiteAction {
  private server: SessionManagementService;

  public static $inject = [
    'ConfirmActionDialog',
    '$translate',
    '$q',
    'SettingsFormService'
  ];

  constructor(
    private ConfirmActionDialog: ConfirmActionDialog,
    private $translate: ng.translate.ITranslateService,
    private $q: ng.IQService,
    private formService: SettingsFormService) {
      this.server = getSessionManagementServiceInstance();
    }

  public execute(parentScope: ng.IScope): ng.IPromise<any> {
    return Sites.instance.getCurrentSiteSettingsForServerAdmin().then((siteSettingsForServerAdmin: ISiteSettingsForServerAdmin) => {
      const dialogScope: ISiteSettingsForServerAdminFormScope = (parentScope.$new(true)) as ISiteSettingsForServerAdminFormScope;

      dialogScope.siteSettings = angular.copy(defaultSiteSettingsForSiteAdmin);
      const copySubscriptionSettings = siteSettingsForServerAdmin.subscriptionSettings;
      if (!_.isUndefined(copySubscriptionSettings)) {
        dialogScope.siteSettings.subscriptionSettings = angular.copy(defaultSubscriptionSettingsForSite);
        dialogScope.siteSettings.subscriptionSettings.serverAllowSubscriptionAttachments =
          copySubscriptionSettings.serverAllowSubscriptionAttachments;
      }

      (dialogScope.siteSettings.siteAdminSettings as ISiteAdminSettings).askDataMode =
        AskDataModeFromString(this.server.defaultAskDataMode());

      dialogScope.sharedForm = this.formService;
      dialogScope.sharedForm.clearForms();

      dialogScope.siteSettingsForServerAdmin = angular.copy(defaultSiteSettingsForServerAdmin);
      dialogScope.siteSettingsForServerAdmin.settings.guestAccessEnabled = ServerSettingsUnauthenticated.isGuestEnabled();
      dialogScope.siteSettingsForServerAdmin.settings.runNowEnabled = FeatureFlags.enableRunNowEnabled ? this.server.isRunNowEnabled() : undefined;

      // Default to derived permissions enabled if cataloging is enabled on the server.
      if (this.server.catalogingOnServerEnabled()) {
        (dialogScope.siteSettings.siteAdminSettings as ISiteAdminSettings).derivedPermissionsEnabled = true;
      }

      dialogScope.isDefaultSite = false;
      dialogScope.hideNameAndId = false;
      // Note that showEncryptionStatus is also being used in React site setting, see ExtractEncryptionSection.
      // When migrating this file away from Angular, make sure showEncryptionStatus = false gets passed properly.
      dialogScope.showEncryptionStatus = false; // because there is no encryption status to be shown for new sites
      dialogScope.createSite = true; // indicate to the scope that the user is creating a new site
      dialogScope.hideAvailability = true;
      dialogScope.hideRevisionHistoryLimitWarning = true;
      dialogScope.showRemoteAgentsSection = false; // Because there would never be new agents for a new site.
      dialogScope.autoSelectSiteName = true;
      return this.ConfirmActionDialog.open({
        scope: dialogScope as ng.IScope,
        title: this.$translate.instant('newSiteDialogTitle'),
        bodyTemplateUrl: 'siteDialog.html',
        actionName: this.$translate.instant('newSiteDialogSubmit'),
        confirmDisabled: () => _.isEmpty(dialogScope.sharedForm) || !dialogScope.sharedForm.state.isValid(),
        action: () => {
          return Sites.instance.createSite(
            dialogScope.siteSettingsForServerAdmin.settings,
            dialogScope.siteSettings.siteAdminSettings as ISiteAdminSettings,
            dialogScope.siteSettings.subscriptionSettings)
            .then(
            () => {
              ToasterService.instance.pop({
                type: ToastType.Info,
                text: this.$translate.instant('siteCreateSuccess', {
                  SITE_NAME: L10n.elideIfLong(dialogScope.siteSettingsForServerAdmin.settings.name)
                })
              });
              this.server.refreshSessionInfo(); // Refresh top bar
            }, (errors: IError[]) => {
              this.errorMessages(errors, dialogScope.siteSettingsForServerAdmin);
              return this.$q.reject();
            });
        },
        size: ModalSize.fitToContent
      }).result.finally(() => {
        dialogScope.sharedForm.clearForms();
        dialogScope.$destroy();
      });
    });
  }

  private errorMessages(errors: IError[], site: ISiteSettingsForServerAdmin): void {
    _.each(errors, (error: IError) => {
      ToasterService.instance.pop({ type: ToastType.Error, text: Sites.instance.errorMessage(error.code, site) });
    });
  }

}

Module.instance().service('CreateSiteAction', CreateSiteAction);
